import { type PreparedShape, type ShapeDefinition, type ShapeSink } from '../shape.js';
import { hash01, onSphere, ramp, ridges, shift, TAU } from './helpers.js';

/* ── Mountains ─────────────────────────────────────────────────────────── */

function peakHeight(x: number, z: number, amplitude: number): number {
  const ridge = Math.max(0, 1.45 - Math.abs(ridges(x, z)));
  const detail = Math.max(0, 0.9 - Math.abs(ridges(x * 2.3 + 11, z * 2.3 - 7))) * 0.35;
  return (Math.pow(ridge, 1.7) + detail) * amplitude;
}

export const mountains: ShapeDefinition = {
  name: 'mountains',
  palette: { base: 0x4a_4e_68, shade: 0x8a_86_a0, accent: 0xf6_fa_ff },
  driftRate: 0.35,
  framing: 1.89,
  prepare: ({ random, total, variant }): PreparedShape => {
    const amplitude = 5.5 + variant * 4;
    const snowLine = amplitude * (0.52 + variant * 0.12);
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const x = (random() - 0.5) * 40;
      const z = (random() - 0.5) * 40;
      sink.x = x;
      sink.z = z;
      if (p < 0.88) {
        const height = peakHeight(x, z, amplitude);
        sink.y = height;
        const snow = ramp(height, snowLine, snowLine + 1.6);
        const rock = ramp(height, 0.4, snowLine);
        sink.tint = snow > 0.02 ? 0.62 + snow * 0.38 : rock * 0.45;
      } else if (p < 0.97) {
        // Cloud banks caught halfway up the range.
        sink.y = amplitude * (0.3 + random() * 0.28);
        sink.tint = 0.5 + random() * 0.2;
        sink.driftX = 0.55;
        sink.driftZ = 0.3;
        sink.phase = hash01(index) * TAU;
      } else {
        // Spindrift streaming off the summits.
        sink.y = peakHeight(x, z, amplitude) + random() * 2.2;
        sink.tint = 0.95;
        sink.driftX = 0.9;
        sink.driftY = 0.3;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Strata: a canyon cut open to show the layers of the earth ─────────── */

const STRATA_DEPTH = 9;

/** Half-width of the gorge at depth `y` — wide at the rim, narrow at the floor. */
function mouth(y: number): number {
  return 2.2 + (1 + y / STRATA_DEPTH) * 3.4;
}

export const strata: ShapeDefinition = {
  name: 'strata',
  palette: { base: 0x8a_40_28, shade: 0xdc_9a_58, accent: 0x9c_e4_ff },
  driftRate: 0.5,
  framing: 1.96,
  prepare: ({ random, total, variant }): PreparedShape => {
    const bands = 7 + Math.floor(variant * 6);
    const gorge = (z: number): number => Math.sin(z * 0.13) * 4 + Math.sin(z * 0.31 + 2) * 1.3;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const z = (random() - 0.5) * 36;
      const centre = gorge(z);
      const side = random() < 0.5 ? -1 : 1;
      if (p < 0.72) {
        // Layered cliff faces. Quantising the tint into bands is what makes
        // the eye read sedimentary rock rather than a gradient.
        const y = -random() * STRATA_DEPTH;
        const band = Math.floor(((-y / STRATA_DEPTH) * bands) % bands);
        sink.x = centre + side * mouth(y) + (random() - 0.5) * 0.5;
        sink.y = y;
        sink.z = z;
        sink.tint = 0.08 + (band / bands) * 0.6 + hash01(band * 31) * 0.08;
      } else if (p < 0.88) {
        // The desert plateau either side of the gorge.
        const away = 1 + random() * 12;
        sink.x = centre + side * (mouth(0) + away);
        sink.y = 0.2 + ridges(sink.x * 0.7, z) * 0.4;
        sink.z = z;
        sink.tint = 0.6 + random() * 0.1;
      } else if (p < 0.96) {
        // The river that did the cutting, far below.
        sink.x = centre + (random() - 0.5) * 3.4;
        sink.y = -STRATA_DEPTH + 0.1 + Math.sin(z * 0.8) * 0.06;
        sink.z = z;
        sink.tint = 0.93 + random() * 0.07;
        sink.driftZ = 0.4;
        sink.phase = z * 0.5;
      } else {
        // Dust hanging in the shafts of light.
        sink.x = centre + (random() - 0.5) * 14;
        sink.y = -random() * STRATA_DEPTH;
        sink.z = z;
        sink.tint = 0.72;
        sink.driftY = 0.35;
        sink.driftX = 0.25;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Dunes ─────────────────────────────────────────────────────────────── */

export const dunes: ShapeDefinition = {
  name: 'dunes',
  palette: { base: 0x8a_54_24, shade: 0xec_b9_6c, accent: 0xff_f0_d0 },
  driftRate: 0.45,
  framing: 1.76,
  prepare: ({ random, total, variant }): PreparedShape => {
    const wind = variant * TAU;
    const crestOf = (x: number, z: number): number => {
      const along = x * Math.cos(wind) + z * Math.sin(wind);
      const across = z * Math.cos(wind) - x * Math.sin(wind);
      const wave = Math.sin(along * 0.22 + Math.sin(across * 0.09) * 1.6);
      // Raising the sine to an odd power sharpens the crest and flattens the
      // lee slope, which is the silhouette that says "sand dune".
      return (wave * 0.5 + 0.5) ** 1.7 * 3.6 + Math.sin(across * 0.4) * 0.18;
    };
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      const x = (random() - 0.5) * 42;
      const z = (random() - 0.5) * 42;
      const height = crestOf(x, z);
      sink.x = x;
      sink.z = z;
      if (p < 0.93) {
        sink.y = height;
        sink.tint = 0.2 + ramp(height, 0.4, 3.4) * 0.55;
      } else {
        // Sand torn off the crest and carried downwind.
        sink.y = height + random() * 1.6;
        sink.tint = 0.9 + random() * 0.1;
        sink.driftX = Math.cos(wind) * 1.1;
        sink.driftZ = Math.sin(wind) * 1.1;
        sink.phase = hash01(index) * TAU;
      }
    };
    return { place };
  },
};

/* ── Planet ────────────────────────────────────────────────────────────── */

const PLANET_RADIUS = 6.2;

export const planet: ShapeDefinition = {
  name: 'planet',
  palette: { base: 0x1a_5a_a8, shade: 0x3c_9a_58, accent: 0xff_ff_ff },
  driftRate: 0.3,
  framing: 2.7,
  prepare: ({ random, total, variant }): PreparedShape => {
    const seaLevel = 0.1 + variant * 0.4;
    const place = (index: number, sink: ShapeSink): void => {
      const p = index / total;
      if (p < 0.72) {
        onSphere(sink, random, PLANET_RADIUS);
        // A continent mask over the sphere: land where the noise runs high.
        const land = ridges(sink.x * 1.1, sink.z * 1.1) + Math.sin(sink.y * 0.55) * 0.5;
        const isLand = land > seaLevel;
        sink.tint = isLand ? 0.46 + random() * 0.2 : random() * 0.07;
        if (isLand) {
          const lift = 1 + 0.012 * (land - seaLevel);
          sink.x *= lift;
          sink.y *= lift;
          sink.z *= lift;
        }
      } else if (p < 0.82) {
        // Weather: banded cloud swirls just above the surface.
        onSphere(sink, random, PLANET_RADIUS * 1.035);
        const band = Math.sin(sink.y * 0.9 + ridges(sink.x, sink.z) * 0.7);
        sink.tint = band > 0.42 ? 0.8 + random() * 0.2 : 0.04;
        sink.driftX = 0.12;
        sink.driftZ = 0.12;
        sink.phase = hash01(index) * TAU;
      } else if (p < 0.88) {
        // Atmosphere: a thin luminous halo.
        onSphere(sink, random, PLANET_RADIUS * (1.07 + random() * 0.05));
        sink.tint = 0.9;
      } else if (p < 0.98) {
        // A debris ring, tilted off the equator.
        const r = PLANET_RADIUS * (1.5 + random() * 0.7);
        const angle = random() * TAU;
        sink.x = Math.cos(angle) * r;
        sink.y = (random() - 0.5) * 0.4;
        sink.z = Math.sin(angle) * r;
        const tilt = 0.35;
        const y = sink.y;
        sink.y = y * Math.cos(tilt) - sink.z * Math.sin(tilt);
        sink.z = y * Math.sin(tilt) + sink.z * Math.cos(tilt);
        sink.tint = 0.6 + random() * 0.3;
      } else {
        // A small moon keeping station.
        onSphere(sink, random, 1.1);
        shift(sink, 11, 4.5, -3);
        sink.tint = 0.55 + random() * 0.2;
      }
    };
    return { place };
  },
};
